import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, SUPPORTED_LANGUAGES } from '@/lib/translations';
import i18n from '@/i18n';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  // Get initial language from localStorage or default to 'en'
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('user_language') || localStorage.getItem('language') || 'en';
  });
  
  const [orgDefaultLanguage, setOrgDefaultLanguage] = useState('en');

  // Sync with i18next on mount
  useEffect(() => {
    // Sync i18n with our language state
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, []);

  // Update language and persist to localStorage
  const setLanguage = useCallback((langCode) => {
    if (translations[langCode]) {
      setCurrentLanguage(langCode);
      localStorage.setItem('user_language', langCode);
      localStorage.setItem('language', langCode); // Also set for i18next
      // Sync with i18next
      i18n.changeLanguage(langCode);
    }
  }, []);

  // Set organization default (called when org data loads)
  const setOrgDefault = useCallback((langCode) => {
    setOrgDefaultLanguage(langCode || 'en');
    // If user hasn't set a preference, use org default
    const userPref = localStorage.getItem('user_language_preference');
    if (!userPref || userPref === 'org_default') {
      setCurrentLanguage(langCode || 'en');
      i18n.changeLanguage(langCode || 'en');
    }
  }, []);

  // Set user preference (can be 'org_default' or a specific language)
  const setUserPreference = useCallback((preference) => {
    localStorage.setItem('user_language_preference', preference);
    if (preference === 'org_default') {
      setCurrentLanguage(orgDefaultLanguage);
      localStorage.setItem('user_language', orgDefaultLanguage);
      localStorage.setItem('language', orgDefaultLanguage);
      i18n.changeLanguage(orgDefaultLanguage);
    } else {
      setCurrentLanguage(preference);
      localStorage.setItem('user_language', preference);
      localStorage.setItem('language', preference);
      i18n.changeLanguage(preference);
    }
  }, [orgDefaultLanguage]);

  // Translation function
  const t = useCallback((key, params = {}) => {
    const lang = translations[currentLanguage] || translations.en;
    let text = lang[key] || translations.en[key] || key;
    
    // Replace parameters like {company}, {days}, etc.
    Object.keys(params).forEach(param => {
      text = text.replace(new RegExp(`{${param}}`, 'g'), params[param]);
    });
    
    return text;
  }, [currentLanguage]);

  // Get terminology based on org settings (customers vs clients)
  const getTerminology = useCallback((orgPreferences) => {
    const mode = orgPreferences?.terminology_mode || 'customers';
    return {
      singular: t(mode === 'clients' ? 'clients' : 'customers').slice(0, -1), // Remove 's'
      plural: t(mode === 'clients' ? 'clients' : 'customers'),
    };
  }, [t]);

  const value = {
    currentLanguage,
    orgDefaultLanguage,
    setLanguage,
    setOrgDefault,
    setUserPreference,
    t,
    getTerminology,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
